import { useTranslation } from 'react-i18next';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { getCasinoGamesByIds, useCasinoGameHistory } from './use-casino-game-history';
import { isCasinoGameAvailableOnPlatform } from './use-casino-games';
import { useSelectedCountryCode } from './use-selected-country-code';

/* 
    Builds a layout object for recently played games based on the user's game history.
*/
export const useRecentlyPlayedGames = () => {
    const { t } = useTranslation('casino');
    const { data: gameHistoryData, isPending: isGameHistoryPending } = useCasinoGameHistory();
    const selectedCountryCode = useSelectedCountryCode();

    return useQuery({
        queryKey: ['recently-played-games', gameHistoryData?.data?.games],
        queryFn: async () => {
            if (!gameHistoryData?.data?.games || gameHistoryData.data.games.length === 0) {
                return null;
            }

            const gameIds = gameHistoryData.data.games.map(game => game.game_id);
            const gamesData = await getCasinoGamesByIds(gameIds, selectedCountryCode);

            // Order games to match the order of gameIds
            const orderedGames = gameIds
                .map(id => gamesData.items.find(game => game.id === id))
                .filter((game): game is (typeof gamesData.items)[0] => game !== undefined)
                .filter(isCasinoGameAvailableOnPlatform);

            return {
                id: 'recently-played',
                name: t('recently_played'),
                flags: ['single-row'],
                items: orderedGames,
            };
        },
        enabled: !isGameHistoryPending,
        placeholderData: keepPreviousData,
    });
};
